import { useCallback, useEffect, useState } from "react";
import { Mail, UserPlus } from "lucide-react";
import type {
  PrequalificationRole,
  PrequalInviteDuplicateConflict,
  SendPrequalInvitePayload,
} from "@/api/prequalification";
import { asDuplicateConflict } from "@/api/prequalification";
import {
  usePartnerBrandOptions,
  useSendPrequalificationInvite,
  useStartInOfficePrequalDraft,
} from "@/hooks/prequalification/usePrequalification";
import {
  Button,
  FormField,
  Input,
  StatusStepper,
  SubmitButton,
  SubmittingHint,
  useFocusTrap,
  type StepperStep,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";

/** Sentinel for the Phoenix-native site. Kept distinct from "" (nothing
 *  picked) because the server refuses a send that names neither a partner
 *  brand nor `phoenixNative` — an unattributed invite is indistinguishable
 *  from organic traffic, which is the whole point of tracking. */
const NATIVE = "native";

const SELECT_CLASS =
  "h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small " +
  "disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed";

/**
 * Which of the three things this same form does.
 *
 * All three collect IDENTICAL input — role, name, email, and the explicit site
 * choice — and differ only in how the delivery decision is made, so they are one
 * component with a variant rather than copies that drift. `invite` is the
 * default, so every existing call site is byte-unchanged.
 *
 * `in-person` starts an IN-OFFICE draft: it mints a suppressed invite (the
 * client is in the room, so nothing is emailed) and opens the form for the staff
 * member to drive. The invite is not a formality — it is what makes the draft
 * INVITED rather than organic, and therefore visible on the initiator's own
 * follow-up list instead of reaching staff as an abandoned stranger.
 *
 * `wizard` offers BOTH from a single button, in two steps: WHO, then HOW. The
 * two legacy variants stay single-step, because the surface that mounts them
 * offers each behind its own separate button and has already made the choice by
 * the time the dialog opens.
 */
export type SendPrequalInviteVariant = "invite" | "in-person" | "wizard";

/** Which action a confirm re-runs, and which mutation's pending flag labels it. */
type PendingAction = "send" | "in-person";

/**
 * WHO → HOW → (CONFIRM). The legacy variants never visit `how`: their delivery
 * method is the variant itself.
 */
type WizardStep = "who" | "how" | "duplicate";

const BASE_STEPS: StepperStep[] = [
  { key: "who", label: "Who", hint: "Who is prequalifying, on which track, and from which site." },
  { key: "how", label: "How", hint: "Email them a link, or start it here with them." },
];

/** Appended ONLY while the server has actually raised the conflict. A step drawn
 *  before it can be reached promises a decision that usually never arrives. */
const CONFIRM_STEP: StepperStep = {
  key: "duplicate",
  label: "Confirm",
  hint: "Someone already invited this person on this site.",
};

const STEP_INDEX: Record<WizardStep, number> = { who: 0, how: 1, duplicate: 2 };

interface BaseProps {
  open: boolean;
  onClose: () => void;
  /** Whether the caller may read the partner-brand list (Partners.ManageBrands).
   *  Without it the picker offers the Phoenix-native site only. */
  canReadBrands: boolean;
  /** Pre-select a partner site when opening from a surface that already knows
   *  which one applies — the Broker / Brokerage record's own Prequalification
   *  tab, where sending from someone else's site is never the intent.
   *
   *  A DEFAULT, not a lock: the picker stays editable, and omitting this keeps
   *  the cross-book queue's behaviour (open with nothing chosen) byte-identical.
   *  The site remains required either way — a staff-sent invite with no site is
   *  refused client-side and again by the server, because a submission that
   *  names no brand is indistinguishable from organic traffic. */
  defaultPartnerBrandId?: string | null;
}

/**
 * `onStarted` is REQUIRED wherever an in-office start is reachable, and absent
 * on the invite-only variant.
 *
 * This modal is a form; where an in-office start goes next is the page's
 * business, and keeping the navigation out of here is what lets it render
 * outside a Router. Optional, it would be a variant that opens a draft and then
 * silently strands it — a real row nobody was taken to, which is worse than a
 * failure because nothing looks wrong.
 */
type Props =
  | (BaseProps & { variant?: "invite"; onStarted?: never })
  | (BaseProps & { variant: "in-person"; onStarted: (draftId: string) => void })
  | (BaseProps & { variant: "wizard"; onStarted: (draftId: string) => void });

const COPY: Record<SendPrequalInviteVariant, {
  title: string;
  description: string;
  action: string;
  siteHelper: string;
}> = {
  invite: {
    title: "Send prequalification invitation",
    description:
      "The recipient gets a tracked link. Their completed submission is attributed back to the site you choose here.",
    action: "Send invitation",
    siteHelper: "Which prequalification site the recipient lands on.",
  },
  "in-person": {
    title: "Start a prequalification in person",
    description:
      "For a client sitting with you. Nothing is emailed — the form opens here and you drive it. Their answers are saved as you go, and it stays on your in-progress list if you have to stop.",
    action: "Start now",
    siteHelper: "Which prequalification site this is attributed to.",
  },
  wizard: {
    title: "New prequalification",
    description:
      "Who is prequalifying, then how you want to run it — email them a link, or start it here with them.",
    action: "Next",
    siteHelper: "Which prequalification site this is attributed to.",
  },
};

export function SendPrequalInviteModal({
  open,
  onClose,
  canReadBrands,
  defaultPartnerBrandId,
  variant = "invite",
  onStarted,
}: Props) {
  const isWizard = variant === "wizard";

  const [step, setStep] = useState<WizardStep>("who");
  const [role, setRole] = useState<PrequalificationRole>("Buyer");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [site, setSite] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Someone already has a live invite out to this buyer on this site. Held as
  // the server's own conflict payload rather than a bare boolean so the confirm
  // step can name them, and so the message the sender reads here is the exact
  // string the server composed — the same one they would see on a refused
  // retry, which is what stops the two drifting apart.
  const [duplicate, setDuplicate] = useState<PrequalInviteDuplicateConflict | null>(null);
  // Which path the confirm step should re-run. An in-office start and a mailed
  // invite both mint an invite and both hit the same gate, so on the wizard the
  // step has to remember which one was pressed — labelling an in-person retry
  // "Sending…" describes the wrong action entirely. Fixed by the variant on the
  // two single-step surfaces, which can only ever do one of them.
  const [pendingAction, setPendingAction] = useState<PendingAction>(
    variant === "in-person" ? "in-person" : "send",
  );

  const brands = usePartnerBrandOptions(open && canReadBrands);
  const activeBrands = (brands.data ?? []).filter((b) => b.isActive);
  const send = useSendPrequalificationInvite();
  const startInPerson = useStartInOfficePrequalDraft();
  const copy = COPY[variant];

  // ONE flag for BOTH paths. Selecting it by variant (as this did) left every
  // field editable underneath an in-person start on any surface that can run
  // both, which is exactly what the wizard is.
  const busy = send.isPending || startInPerson.isPending;

  // Reset on each open so a previous attempt never bleeds into the next one. The
  // site seeds from the caller's default when it supplied one; with none it
  // clears, exactly as before.
  useEffect(() => {
    if (!open) return;
    setStep("who");
    setRole("Buyer");
    setFirstName("");
    setLastName("");
    setEmail("");
    setSite(defaultPartnerBrandId ?? "");
    setError(null);
    setDuplicate(null);
    setPendingAction(variant === "in-person" ? "in-person" : "send");
  }, [open, defaultPartnerBrandId, variant]);

  // Modal manages focus itself since 2026-08-02, but its trap is keyed only on
  // `open`. This is a WIZARD: each step has to open on its own first field, which
  // needs the `focusKey` re-run. So this keeps its own trap and Modal stands down
  // via `manageFocus={false}` — two nested traps would both handle Tab and move
  // focus twice at the cycle boundary.
  //
  // Modal's X button sits outside this container, which is why the footer below
  // always carries a real Cancel.
  const trap = useFocusTrap<HTMLDivElement>({ active: open, focusKey: step });

  /** Guarded against closing mid-submit. All three of Modal's close routes
   *  (Escape, backdrop, X) funnel through this single prop, so a caller-side
   *  guard is complete rather than partial. */
  const handleRequestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const ready =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    site.length > 0;

  // Turn a failed attempt into either the duplicate step or an inline error. The
  // 409 is the ONLY status that opens the confirm step; a 429 (daily cap) and
  // everything else stay inline ON THE STEP THAT RAISED THEM, wizard open and
  // values intact — a cap is a terminal refusal whose message tells the sender
  // to ask someone else to send, and closing would take away the address they
  // need in order to do that.
  const handleSendError = (e: unknown) => {
    const conflict = asDuplicateConflict(e);
    if (conflict) {
      setError(null);
      setDuplicate(conflict);
      setStep("duplicate");
      return;
    }
    setError(extractErrorMessage(e));
  };

  /** Identical on both paths. Built once so the two delivery methods, the
   *  confirm retry, and all three variants cannot send different things. */
  const inviteBody = (confirmDuplicate: boolean): SendPrequalInvitePayload => ({
    role,
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    email: email.trim(),
    partnerBrandId: site === NATIVE ? null : site,
    phoenixNative: site === NATIVE,
    // Default false, so the FIRST attempt always gets the warning. Only the
    // confirm button re-submits with true.
    confirmDuplicate,
  });

  const run = (action: PendingAction, confirmDuplicate: boolean) => {
    // Kept as an inline error rather than a disabled button on the single-step
    // variants: that is what they have always done, and a silently dead primary
    // action explains nothing.
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError("First name, last name and email are all required.");
      return;
    }
    if (!site) {
      setError("Choose which site this invitation should come from.");
      return;
    }
    setError(null);
    setPendingAction(action);
    const body = inviteBody(confirmDuplicate);

    if (action === "in-person") {
      startInPerson.mutate(body, {
        onSuccess: (started) => {
          setDuplicate(null);
          onClose();
          // Straight into the form — the page owns where that is.
          onStarted?.(started.draftId);
        },
        onError: handleSendError,
      });
      return;
    }

    send.mutate(body, {
      onSuccess: () => {
        setDuplicate(null);
        onClose();
      },
      onError: handleSendError,
    });
  };

  /** The single-step variants submit straight from the footer; the wizard's
   *  footer advances instead, and its two cards submit. */
  const submitDefault = () => run(variant === "in-person" ? "in-person" : "send", false);

  function goNext() {
    if (!isWizard) {
      submitDefault();
      return;
    }
    if (!ready) return;
    setError(null);
    setStep("how");
  }

  function goBack() {
    setError(null);
    if (step === "duplicate") {
      // Back out of the CONFLICT, not out of the wizard. Every typed value
      // survives: the sender may want to correct the address, or leave it to the
      // colleague who already owns the invite. Clearing the warning is what drops
      // the third step from the stepper again — the server is re-asked on the
      // next attempt rather than the answer being cached here.
      setDuplicate(null);
      setStep(isWizard ? "how" : "who");
      return;
    }
    setStep("who");
  }

  /** Proceed over the warning, through the path actually chosen. The server
   *  records the override with the prior sender named — it is not a
   *  suppression.
   *
   *  The conflict is deliberately NOT cleared here. Clearing it up front would
   *  blank the message the sender is reading for the whole in-flight window,
   *  since this step renders from it; `run`'s own success handler clears it, and
   *  a second 409 simply re-seats the same payload. */
  function confirmDuplicateSend() {
    run(pendingAction, true);
  }

  const steps = duplicate ? [...BASE_STEPS, CONFIRM_STEP] : BASE_STEPS;

  const errorLine = error ? (
    <p className="mt-space-3 mb-0 text-small text-status-error" role="alert">
      {error}
    </p>
  ) : null;

  // Rendered INSIDE the trapped container below, NOT through Modal's `footer`
  // prop. That prop renders its bar as a SIBLING of the children, so a trap
  // scoped to the body would exclude Cancel and Next — a cycle that cannot reach
  // the only way forward or out is worse than no trap at all. Precedent for a
  // self-rendered footer is the portal's own wizard.
  const footer = (
    <div className="mt-space-4 flex items-center justify-end gap-space-2 border-t border-border-light pt-space-3">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleRequestClose}
        disabled={busy}
        data-testid="prequal-invite-cancel"
      >
        Cancel
      </Button>
      {step === "who" ? (
        <SubmitButton
          size="sm"
          pending={!isWizard && busy}
          pendingLabel={pendingAction === "in-person" ? "Opening…" : "Sending…"}
          // The wizard gates its Next on completeness, so nobody picks a delivery
          // method before being told they cannot use either. The single-step
          // variants keep their always-enabled primary and their inline message.
          disabled={busy || (isWizard && !ready)}
          onClick={() => goNext()}
          data-testid="prequal-invite-primary"
        >
          {copy.action}
        </SubmitButton>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={goBack}
          disabled={busy}
          data-testid="prequal-invite-back"
        >
          Back
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      title={copy.title}
      description={copy.description}
      size="md"
      manageFocus={false}
    >
      <div
        ref={trap.containerRef}
        onKeyDown={trap.onKeyDown}
        className="flex flex-col"
        data-testid="prequal-invite-body"
      >
        {/* Only the wizard is staged. A two-pill stepper over a single-step form
            would announce a journey that surface does not have. */}
        {isWizard && (
          <StatusStepper
            steps={steps}
            currentIndex={STEP_INDEX[step]}
            label="New prequalification progress"
            className="mb-space-4"
            data-testid="prequal-invite-steps"
          />
        )}

        {step === "who" && (
          <div className="flex flex-col gap-space-3">
            <FormField label="Role" required htmlFor="invite-role">
              <select
                id="invite-role"
                ref={trap.setInitialFocus}
                className={SELECT_CLASS}
                value={role}
                disabled={busy}
                onChange={(e) => setRole(e.target.value as PrequalificationRole)}
              >
                <option value="Buyer">Buyer</option>
                <option value="Seller">Seller</option>
              </select>
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
              <FormField label="First name" required htmlFor="invite-first-name">
                <Input
                  id="invite-first-name"
                  value={firstName}
                  disabled={busy}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </FormField>
              <FormField label="Last name" required htmlFor="invite-last-name">
                <Input
                  id="invite-last-name"
                  value={lastName}
                  disabled={busy}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </FormField>
            </div>

            <FormField label="Email" required htmlFor="invite-email">
              <Input
                id="invite-email"
                type="email"
                value={email}
                disabled={busy}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormField>

            {/* Attribution, not delivery — which is why it belongs on WHO rather
                than beside the two delivery cards. It is needed identically on
                both paths, and it is part of what "complete" means before either
                becomes offerable. */}
            <FormField
              label="Site"
              required
              htmlFor="invite-site"
              helperText={
                canReadBrands ? copy.siteHelper : "Only the Phoenix Capital site is available to you."
              }
            >
              <select
                id="invite-site"
                className={SELECT_CLASS}
                value={site}
                disabled={busy}
                onChange={(e) => setSite(e.target.value)}
              >
                <option value="">Select a site…</option>
                <option value={NATIVE}>Phoenix Capital (native)</option>
                {activeBrands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.displayName}
                  </option>
                ))}
              </select>
            </FormField>

            {isWizard && !ready && (
              <p className="m-0 text-caption text-gray-mid">
                Their name, email and a site are needed either way &mdash; the in-person
                start records the same details.
              </p>
            )}

            {errorLine}
            {footer}
          </div>
        )}

        {step === "how" && (
          <>
            <div className="grid gap-space-3 sm:grid-cols-2">
              <div className="flex flex-col rounded-md border border-border-light bg-white-pure p-space-3">
                <h3 className="m-0 font-display text-lead font-bold text-onyx">
                  Email them a link
                </h3>
                <p className="mt-space-1 mb-space-3 flex-1 text-caption text-gray-mid">
                  We&rsquo;ll email {email.trim() || "them"} a tracked link. They fill it in
                  whenever they like, and the result is attributed to the site you chose.
                </p>
                <SubmitButton
                  size="sm"
                  ref={trap.setInitialFocus}
                  pending={send.isPending}
                  pendingLabel="Sending…"
                  disabled={busy}
                  onClick={() => run("send", false)}
                  data-testid="prequal-invite-send"
                >
                  <Mail size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
                  Send invitation
                </SubmitButton>
              </div>

              {/* The same decision made a different way, so it sits beside the
                  first rather than under it. */}
              <div className="flex flex-col rounded-md border border-border-light bg-white-pure p-space-3">
                <h3 className="m-0 font-display text-lead font-bold text-onyx">
                  They&rsquo;re with me now
                </h3>
                <p className="mt-space-1 mb-space-3 flex-1 text-caption text-gray-mid">
                  The form opens here for you to drive &mdash; nothing is emailed, and
                  their answers are saved as you go.
                </p>
                <SubmitButton
                  variant="secondary"
                  size="sm"
                  pending={startInPerson.isPending}
                  pendingLabel="Opening…"
                  disabled={busy}
                  onClick={() => run("in-person", false)}
                  data-testid="prequal-invite-start-in-person"
                >
                  <UserPlus size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
                  Start in person
                </SubmitButton>
              </div>
            </div>

            {errorLine}
            <SubmittingHint pending={busy} message="One moment…" className="mt-space-3" />
            {footer}
          </>
        )}

        {step === "duplicate" && (
          <>
            {/* Somebody already has a live invite out to this buyer on this site.
                A WARNING, not a rule: two people chasing one buyer is a
                coordination problem, and the second sender may well be the right
                one to proceed.

                A STEP, not a nested dialog. The shared Modal has no focus
                management of its own, so a modal inside a modal is a real
                keyboard trap — the inner overlay paints on top while Tab still
                walks the outer one. As a step there is one dialog, one focus
                scope, one Back button, and the typed values sit untouched in
                state behind it, so backing out returns the sender to their filled
                form rather than losing it.

                The server's own message renders verbatim. Composing a second one
                here would let the warning before the click and the error on a
                refused retry drift apart, which is exactly the failure the
                server-side composition exists to prevent. */}
            <div
              ref={trap.setInitialFocus}
              tabIndex={-1}
              role="alert"
              className="rounded-md border border-status-warning/40 bg-status-warning/10 p-space-3"
              data-testid="prequal-invite-duplicate-warning"
            >
              <p className="m-0 text-small text-onyx">{duplicate?.message}</p>
              <p className="mt-space-2 mb-0 text-caption text-gray-mid">
                Sending anyway is fine &mdash; they will simply receive a second link.
              </p>
              <div className="mt-space-3">
                <SubmitButton
                  size="sm"
                  pending={busy}
                  // The path actually chosen, not a fixed verb: an in-office start
                  // opens a form and sends nothing.
                  pendingLabel={pendingAction === "in-person" ? "Opening…" : "Sending…"}
                  onClick={confirmDuplicateSend}
                  data-testid="prequal-invite-duplicate-confirm"
                >
                  Send anyway
                </SubmitButton>
              </div>
            </div>

            {errorLine}
            <SubmittingHint pending={busy} message="One moment…" className="mt-space-3" />
            {footer}
          </>
        )}
      </div>
    </Modal>
  );
}
